/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import PanelBoxLayout from 'components/layout/panelBox';
import RowAction from 'components/rowAction';
import StatusChip from 'components/statusChip';
import { useActions } from 'common/hooks/useActions';
import { RejectReasonOwnerType } from 'common/enums';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

import * as S from './styles';
import * as T from 'common/types';

interface IProps {
  menuList: { list: T.Pages.CafeteriaListItem[]; totalPages: number };
  loading: boolean;
}

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const ReasonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 12a8 8 0 0 1-11.5 7.2L3 21l1.8-6.5A8 8 0 1 1 21 12z" />
  </svg>
);

export default function TeaboyReasonsTable({ menuList, loading }: IProps) {
  const { rejectReasonsListRequestAction } = useActions();
  const [meta] = useState({
    keyword: '',
    ownerType: RejectReasonOwnerType.teaboy
  });

  const fetchData = () => {
    rejectReasonsListRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 84 },
            { id: 3, key: 'title', title: 'Reason', dir: 'left' },
            /* "CreatedAt" and "UpdatedAt" were the payload's key names in a
               table header. They are the words for what they hold now. */
            { id: 4, key: 'CreatedAt', title: 'Created', dir: 'left', width: 156 },
            { id: 5, key: 'UpdatedAt', title: 'Updated', dir: 'left', width: 156 },
            { id: 6, key: 'status', title: 'Status', dir: 'left', width: 120 },
            { id: 7, key: 'edit', title: '', type: 'actions', width: 64 }
          ]}
          style={{ minWidth: 'auto' }}
          data={menuList?.list}
          loading={loading}
          empty={
            <Empty
              icon={<ReasonIcon />}
              title="No reasons yet"
              text="Add the reasons tea boys can pick from when they cancel an order."
            />
          }
          altanfeethiFields={{
            id: (item: T.Pages.RejectReasons) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),
            /*
             * Was a `.map()` over both translations returning `false` for the
             * Arabic one — React drops a false child, so it rendered, but the
             * cell was picking a value by side effect. It finds it now.
             */
            title: (item: T.Pages.RejectReasons) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>
                  {item?.reasonTranslations?.find((t) => t.language === 'en')?.translate}
                </Value>
              </S.Div>
            ),

            CreatedAt: (item: T.Pages.RejectReasons) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.createdAt)}</Value>
              </S.Div>
            ),
            UpdatedAt: (item: T.Pages.RejectReasons) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.updatedAt)}</Value>
              </S.Div>
            ),
            /*
             * `Active` in `#1DBAAF` and `InActive` in `#FF6464` — two colours
             * off the palette, and a word that is not one. The `menuItem`
             * domain already maps this exact boolean.
             */
            status: (item: T.Pages.RejectReasons) => (
              <S.Div className="text-left">
                <StatusChip domain="menuItem" value={!!item?.isActive} />
              </S.Div>
            ),
            edit: (item: T.Pages.RejectReasons) => (
              <RowAction to={`/panel/cafeteria/teaboy-cancelled-reason/${item?.id}`} label="Edit reason">
                <PenIcon />
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
    </S.Content>
  );
}
